import { ArrayUtil } from 'helper-util';
import {
  formatDate,
  formatDateArray,
  formatDateArrayTz,
  formatWithDateAndTime,
  formatWithTime,
  formatYear,
} from '../../utils/dateUtil';
import {
  formatWithFourDecimals,
  formatWithSeparator,
  formatWithSeparatorAndDecimals,
  formatWithSeparatorAndSixDecimals,
  formatWithSixDecimals,
  formatWithTwoDecimals,
} from '../../utils/formatUtils';
import { ColDef } from 'ag-grid-community';

export const getParams = (fileName, skipParentExportHeader, formatWithDateAndTime) => {
  return {
    fileName,
    allColumns: true,
    columnGroups: true,
    skipHeader: skipParentExportHeader,
    /* Skip the row if table has row grouping and the row is a parent row.
			 We only want child rows in excel since parent rows wont have the grouping field. */
    shouldRowBeSkipped: (params) => {
      if (params && params.node && params.node.key) {
        return true;
      } else {
        return false;
      }
    },

    processCellCallback: (params) => {
      if (skipParentExportHeader) {
        return '';
      }
      const cellFormatingType = params.column.colDef.excelFormat;
      if (cellFormatingType) {
        const formatFn = cellFormatingType === 'dateTime' && formatWithDateAndTime;
        return params.value && formatFn ? formatFn(params.value) : 'N/A';
      }
      return params.value;
    },
  };
};

const calculateTotal = (field: any, gridApi: any, col: any) => {
  let total = 0;
  if (col.type === 'numericColumn' && col.noFooter !== true) {
    gridApi.forEachNodeAfterFilterAndSort((rowNode) => {
      const value = Number(rowNode.data && rowNode.data[col.field]);
      total += isNaN(value) ? 0 : value;
    });
  }

  field[col.field] =
    col.type === 'numericColumn'
      ? col.rounding
        ? Number(total.toFixed(2))
        : Math.round(total)
      : '';
};

export const getPinnedBottomRowData = (gridApi: any, column: any[]) => {
  const field = {};
  column.forEach((col) => {
    if (col.children) {
      col.children.forEach((childCol) => {
        calculateTotal(field, gridApi, childCol);
      });
    } else {
      calculateTotal(field, gridApi, col);
    }
  });
  return [field];
};

export const commaNoDecimal = {
  type: 'numericColumn',
  cellRenderer: (params) => {
    return params.value !== null ? formatWithSeparator(Number(params.value)) : null;
  },
};
export const commaSixDecimal = {
  type: 'numericColumn',
  cellRenderer: (params) => {
    return params.value !== null ? formatWithSeparatorAndSixDecimals(Number(params.value)) : null;
  },
};
export const commaTwoDecimal = {
  type: 'numericColumn',
  cellRenderer: (params) => {
    return params.value !== null ? formatWithSeparatorAndDecimals(Number(params.value)) : null;
  },
};
export const commaTwoDecimalPercent = {
  type: 'numericColumn',
  cellRenderer: (params) => {
    return params.value !== null
      ? formatWithSeparatorAndDecimals(Number(params.value) * 100)
      : null;
  },
};
export const fontWeight = {
  cellStyle: { 'font-weight': 'bold' },
};
export const noCommaTwoDecimalPercent = {
  type: 'numericColumn',
  cellRenderer: (params) => {
    return params.value !== null ? formatWithTwoDecimals(Number(params.value) * 100) : null;
  },
};
export const noCommaTwoDecimal = {
  type: 'numericColumn',
  cellRenderer: (params) => {
    return params.value !== null ? formatWithTwoDecimals(Number(params.value)) : null;
  },
};
export const noCommaFourDecimal = {
  type: 'numericColumn',
  cellRenderer: (params) => {
    return params.value !== null ? formatWithFourDecimals(Number(params.value)) : null;
  },
};
export const noCommaNoDecimal = {
  type: 'numericColumn',
  cellRenderer: (params) => {
    return params.value !== null ? Math.round(params.value) : null;
  },
};
export const noCommaSixDecimal = {
  type: 'numericColumn',
  cellRenderer: (params) => {
    return params.value !== null ? formatWithSixDecimals(Number(params.value)) : null;
  },
};
export const noPinnedFooter = {
  noFooter: true,
  pinnedRowCellRenderer: (params) => {
    const { style, value } = params;
    const eGui = document.createElement('div');
    if (!ArrayUtil.isEmpty(style)) {
      style.forEach((property) => {
        eGui.style[property] = style[property];
      });
    }
    eGui.innerHTML = value;
  },
};
export const numberOnly = {
  type: 'numericColumn',
  cellRenderer: (params) => {
    return Number.isNaN(Number(params.value)) ? null : Number(params.value);
  },
};
export const successDangerClass = (reverseColor: boolean = false) => {
  return {
    cellClass: (params) => {
      const cellClass = ['ag-right-aligned-cell'];
      if (Number(params.value) > 0) cellClass.push(reverseColor ? 'text-danger' : 'text-success');
      if (Number(params.value) < 0) cellClass.push(reverseColor ? 'text-success' : 'text-danger');
      return cellClass;
    },
  };
};

/* Date Time Formatter */
export const yyyyMmDd = {
  cellRenderer: (params) => {
    return params.value ? formatDate(params.value) : null;
  },
};

export const yyyy = {
  cellRenderer: (params) => {
    return params.value ? formatYear(params.value) : null;
  },
};

export const yyyyMMDdHHmmss = {
  cellRenderer: (params) => {
    return params.value ? formatDateArray(params.value) : null;
  },
};

export const yyyyMMDdHHmmssTz = {
  cellRenderer: (params) => {
    return params.value ? formatDateArrayTz(params.value, params.colDef.zone) : null;
  },
};

export const HHmmss = {
  cellRenderer: (params) => {
    return params.value ? formatWithTime(params.value) : null;
  },
};
export const yyyyMmDdHHmmss = {
  cellRenderer: (params) => {
    return params.value ? formatWithDateAndTime(params.value) : null;
  },
};

export const pinnedLeft = {
  pinned: 'left',
  lockPosition: true,
  lockPinned: true,
  rowDrag: false,
  width: 100,
};
export const noColumnAction = {
  filter: false,
  resizable: false,
  sortable: false,
  menuTabs: [],
};
export const iconWidth = {
  minWidth: 55,
  width: 55,
  maxWidth: 55,
};

export const getSavePayload = (gridLayoutId: string, gridLayout: any, configData: any) => {
  const payload = {
    screenId: gridLayoutId,
    name: 'default',
    layout: JSON.stringify(gridLayout),
  };
  if (configData[gridLayoutId]) {
    const userLayoutId = configData[gridLayoutId][0].userLayoutId;
    payload['userLayoutId'] = userLayoutId;
  }
  return payload;
};

export const makeSummaryRow = (columnDefs: ColDef[], gridApi: any) =>
  (columnDefs || []).reduce(
    (row, col) => {
      const field = col.field as string | undefined;
      const showSummaryTotal = (col as any).showSummaryTotal;

      if (!field || !showSummaryTotal) return row;

      let hasNumericValue = false;
      let sum = 0;

      gridApi.forEachNodeAfterFilterAndSort((node) => {
        if (node.rowPinned) return;

        let value: any;

        if (typeof col.valueGetter === 'function') {
          value = col.valueGetter({
            data: node.data,
            node,
            colDef: col,
            column: (gridApi as any).getColumn(field),
            api: gridApi,
          } as any);
        } else {
          value = node.data && node.data[field];
        }

        const num = typeof value === 'number' ? value : NaN;
        if (!Number.isNaN(num)) {
          hasNumericValue = true;
          sum += num;
        }
      });
      //TODO-- Change the logic to handle the decimal places based on the column definition
      row[field] = hasNumericValue ? formatWithSeparatorAndDecimals(sum) : '';
      return row;
    },
    {} as Record<string, any>,
  );

export const applyStateAndPinnedRowConfig = (
  columnDefs: ColDef[],
  savedColumnState: any[] | null,
) => {
  const stateMap: Map<string, any> | null = savedColumnState
    ? new Map(savedColumnState.map((col, index) => [col.colId, { ...col, order: index }]))
    : null;

  const mergedDefs = columnDefs.map((col) => {
    if (!stateMap) return col;
    const colId = col.colId || col.field;
    const state = stateMap.get(colId);
    if (!state) return col;
    return {
      ...col,
      hide: state.hide,
      pinned: state.pinned,
      width: state.width,
      rowGroup: state.rowGroup,
    };
  });

  const withPinnedRowHandling = mergedDefs.map((col) => {
    const editable = (params) => {
      if (params?.node?.rowPinned) return false;
      if (typeof col.editable === 'function') return col.editable(params);
      return !!col.editable;
    };

    let wrappedValueGetter = col.valueGetter;
    if (typeof col.valueGetter === 'function') {
      const originalValueGetter = col.valueGetter;
      wrappedValueGetter = (params: any) => {
        if (params?.node?.rowPinned) {
          const field = col.field as string | undefined;
          return field ? params.data?.[field] : undefined;
        }
        return originalValueGetter(params);
      };
    }

    if (col.cellRendererSelector) {
      return {
        ...col,
        editable,
        valueGetter: wrappedValueGetter,
        cellRendererSelector: (params) => {
          if (params?.node?.rowPinned) return undefined;
          return col.cellRendererSelector(params);
        },
      };
    }

    if (col.cellRenderer) {
      const isFunctionRenderer = typeof col.cellRenderer === 'function';

      if (isFunctionRenderer) {
        const originalRenderer = col.cellRenderer;
        return {
          ...col,
          editable,
          valueGetter: wrappedValueGetter,
          cellRenderer: (params) => {
            if (params?.node?.rowPinned) {
              return params.value;
            }
            return originalRenderer(params);
          },
        };
      }

      return {
        ...col,
        editable,
        valueGetter: wrappedValueGetter,
        cellRendererSelector: (params) => {
          if (params?.node?.rowPinned) return undefined;
          return { component: col.cellRenderer };
        },
        cellRenderer: undefined,
      };
    }

    return { ...col, editable, valueGetter: wrappedValueGetter };
  });

  if (!stateMap) return withPinnedRowHandling;

  return withPinnedRowHandling.sort((a, b) => {
    const aOrder = stateMap.get(a.colId || a.field)?.order;
    const bOrder = stateMap.get(b.colId || b.field)?.order;
    if (aOrder === undefined && bOrder === undefined) return 0;
    if (aOrder === undefined) return 1;
    if (bOrder === undefined) return -1;
    return aOrder - bOrder;
  });
};

export const filterByDateTreeList = {
  filter: 'agSetColumnFilter',
  filterParams: {
    treeList: true,
    treeListPathGetter: (value: any) => {
      if (!value) return [];

      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return [String(value)];
      }

      const year = String(date.getFullYear());
      const month = date.toLocaleString('default', { month: 'long' });
      const day = String(date.getDate()).padStart(2, '0');

      return [year, month, day];
    },
  },
  filterValueGetter: (params) => {
    if (!params?.data) return null;

    const { modifiedDate, maturityDate, lastUpdate } = params.data;

    if (modifiedDate) return modifiedDate;
    if (maturityDate) return maturityDate;
    if (lastUpdate) return lastUpdate;

    return null;
  },
};
